/**
 * Convite recebido.
 *
 * Chega de duas formas: link do WhatsApp (`/convite?token=…`) ou código
 * digitado. Sem nenhum dos dois, a tela pede o código — que é o caso de quem
 * recebeu por voz ou por print.
 *
 * O que aparece antes de aceitar é o mínimo: nome da viagem e quem convidou.
 * Quem tem o link ainda não é membro, e a lista de participantes já seria
 * informação do grupo.
 */
import { calendarDate, formatDateRange, formatInviteCode, normalizeInviteCode } from '@nv/core';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { acceptInvitationAsMe, ApiError, previewInvitation, type InvitationPreview } from '../../auth/api';
import { useAuthStore } from '../../auth/store';
import { Icon } from '../../ui/icons';
import { Card, ErrorState, Skeleton } from '../../ui/primitives';
import { useItinerary } from '../roteiro/store';
import { Field } from './Field';

function periodoDoConvite(c: InvitationPreview): string {
  try {
    return formatDateRange(calendarDate(c.startDate), calendarDate(c.endDate));
  } catch {
    return `${c.startDate} → ${c.endDate}`;
  }
}

export function ConviteScreen() {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const status = useAuthStore((s) => s.status);
  const refresh = useAuthStore((s) => s.refresh);
  const autenticado = status === 'authenticated';

  // Esta tela é pública e pode ser a PRIMEIRA a abrir — link do convite vindo
  // do WhatsApp. Só as rotas protegidas resolviam a sessão no boot; aqui o
  // status ficava `unknown` e quem já tinha conta era mandado criar outra.
  useEffect(() => {
    if (status === 'unknown') void refresh();
  }, [status, refresh]);

  const token = params.get('token') ?? undefined;
  const codigoDaUrl = params.get('code') ?? undefined;

  const [codigo, setCodigo] = useState(codigoDaUrl ?? '');
  const [convite, setConvite] = useState<InvitationPreview | null>(null);
  const [carregando, setCarregando] = useState(Boolean(token || codigoDaUrl));
  const [erro, setErro] = useState<string | null>(null);
  const [entrando, setEntrando] = useState(false);

  async function abrir(localizador: { token?: string; code?: string }) {
    setCarregando(true);
    setErro(null);
    try {
      setConvite(await previewInvitation(localizador));
    } catch (e) {
      setConvite(null);
      setErro(e instanceof ApiError ? e.message : 'Não foi possível abrir o convite.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (token) void abrir({ token });
    else if (codigoDaUrl) {
      const normalizado = normalizeInviteCode(codigoDaUrl);
      if (normalizado) void abrir({ code: normalizado });
      else {
        setCarregando(false);
        setErro('Código inválido. Confira as seis letras e números.');
      }
    }
    // Só na montagem: o localizador vem da URL e não muda sozinho.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function enviarCodigo(evento: FormEvent) {
    evento.preventDefault();
    const normalizado = normalizeInviteCode(codigo);
    if (!normalizado) {
      setErro('Código inválido. São oito letras e números, como A7K2-M9XQ.');
      return;
    }
    void abrir({ code: normalizado });
  }

  /** Quem já tem conta aceita direto; quem não tem passa por criar conta. */
  async function entrarNaViagem() {
    const localizador = token ? { token } : { code: normalizeInviteCode(codigo) ?? '' };

    if (!autenticado) {
      const query = new URLSearchParams(
        token ? { token } : { code: localizador.code ?? '' },
      );
      navegar(`/criar-conta?${query.toString()}`);
      return;
    }

    setEntrando(true);
    try {
      const { tripId } = await acceptInvitationAsMe(localizador);
      // Confirmação de que entrou (PO, 19/09): o toast aparece na Home da viagem.
      useItinerary.getState().showToast(`Você entrou em ${convite?.tripName ?? 'na viagem'}. Bem-vindo(a)!`, 6000);
      navegar(`/?trip=${tripId}`, { replace: true });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível entrar na viagem.');
    } finally {
      setEntrando(false);
    }
  }

  if (carregando) {
    return (
      <section className="auth-screen">
        <Skeleton height={28} count={3} />
      </section>
    );
  }

  if (convite) {
    return (
      <section className="auth-screen">
        <div className="auth-kicker">VOCÊ FOI CONVIDADO PARA</div>
        <h1 className="auth-hero">{convite.tripName}</h1>
        <p className="auth-subtitle">por {convite.invitedByName}</p>
        {/* A viagem reconhecível antes de entrar (PO, 19/09): capa, destino e período. */}
        <div className={convite.coverUrl ? 'auth-cover auth-cover--foto' : 'auth-cover auth-cover--sem'} data-testid="convite-capa">
          {convite.coverUrl ? <img src={convite.coverUrl} alt="" /> : <Icon name="mapa" size={36} />}
        </div>
        <p className="auth-subtitle convite__resumo" data-testid="convite-resumo">
          {convite.primaryDestination} · {periodoDoConvite(convite)}
        </p>

        <button
          type="button"
          className="nv-btn nv-btn--primary nv-btn--block"
          onClick={() => void entrarNaViagem()}
          // Enquanto não se sabe se há sessão, a decisão "aceitar ou criar
          // conta" ainda não pode ser tomada.
          disabled={entrando || status === 'unknown'}
        >
          {entrando ? 'Entrando…' : status === 'unknown' ? 'Verificando…' : 'Entrar na viagem'}
        </button>

        {erro && (
          <p className="auth-error" role="alert">
            {erro}
          </p>
        )}

        <Card variant="chip">
          <p className="auth-note">
            Ao entrar, quem já está na viagem passa a ver seu nome. Convite é de
            uso único e vale até {new Date(convite.expiresAt).toLocaleDateString('pt-BR')}.
          </p>
        </Card>
      </section>
    );
  }

  return (
    <section className="auth-screen">
      <h1 className="auth-title">Tenho um convite</h1>
      <p className="auth-subtitle">
        Cole o link que você recebeu ou digite o código de seis caracteres.
      </p>

      {erro && (
        <ErrorState
          title="Convite não disponível"
          description={erro}
          onRetry={() => setErro(null)}
        />
      )}

      <form className="auth-form" onSubmit={enviarCodigo}>
        <Field
          label="Código do convite"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder={formatInviteCode('A7K2M9XQ')}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
        />
        <button type="submit" className="nv-btn nv-btn--primary nv-btn--block">
          Abrir convite
        </button>
      </form>
    </section>
  );
}
